import { useEffect, useRef, useState } from 'react';

import { getAPI } from '../store/SmartCreateStore';

declare global {
	interface SpeechRecognition extends EventTarget {
		continuous: boolean;
		interimResults: boolean;
		lang: string;
		start(): void;
		stop(): void;
		onresult: ((event: SpeechRecognitionEvent) => void) | null;
		onerror: ((event: any) => void) | null;
		onend: (() => void) | null;
	}

	interface SpeechRecognitionConstructor {
		new (): SpeechRecognition;
	}

	interface Window {
		SpeechRecognition?: SpeechRecognitionConstructor;
		webkitSpeechRecognition?: SpeechRecognitionConstructor;
	}
}

interface SpeechRecognitionEvent {
	resultIndex: number;
	results: {
		[index: number]: {
			[index: number]: {
				transcript: string;
			};
			isFinal: boolean;
			length: number;
		};
		length: number;
	};
}

const checkSpeechRecognitionSupport = (): boolean => {
	if (typeof window === 'undefined') {
		return false;
	}
	return !!(window.SpeechRecognition || window.webkitSpeechRecognition);
};

export const useVoiceInput = ({ inputMethod }: { inputMethod: string | null }) => {
	const editorApi = getAPI();
	// TODO: Maybe move isListening to the Store
	const [isListening, setIsListening] = useState(false);
	const recognitionRef = useRef<SpeechRecognition | null>(null);
	const [errorMessage, setErrorMessage] = useState<string>('');
	const interimTextRef = useRef<{ from: number; to: number; text: string } | null>(null);

	useEffect(() => {
		if (inputMethod === 'speech') {
			setIsListening(true);
		} else {
			setIsListening(false);
		}
	}, [inputMethod]);

	useEffect(() => {
		const startRecognition = () => {
			if (!checkSpeechRecognitionSupport()) {
				return;
			}

			if (recognitionRef.current) {
				try {
					recognitionRef.current.stop();
				} catch (error) {
					setErrorMessage('Failed to stop speech recognition');
				}
				recognitionRef.current = null;
			}

			try {
				const SpeechRecognitionAPI = (window.webkitSpeechRecognition || window.SpeechRecognition)!;
				const recognition = new SpeechRecognitionAPI();

				recognitionRef.current = recognition;
				recognition.continuous = true;
				recognition.interimResults = true;
				recognition.lang = 'en-US';

				recognition.onresult = (event: SpeechRecognitionEvent) => {
					let finalTranscript = '';
					let interimTranscript = '';

					// Process only new results starting from resultIndex
					for (let i = event.resultIndex; i < event.results.length; i++) {
						const transcript = event.results[i][0].transcript;
						if (event.results[i].isFinal) {
							finalTranscript += transcript + ' ';
						} else {
							interimTranscript += transcript;
						}
					}

					editorApi?.core.actions.execute(({ tr }) => {
						const { selection } = tr;
						let pos = selection.$head.pos;

						// If we have interim text currently displayed, remove it first
						if (interimTextRef.current) {
							tr.delete(interimTextRef.current.from, interimTextRef.current.to);
							// Adjust position if we deleted text before the cursor
							if (interimTextRef.current.from < pos) {
								pos -= interimTextRef.current.to - interimTextRef.current.from;
							}
							interimTextRef.current = null;
						}

						if (finalTranscript) {
							// Insert final transcript
							tr.insertText(finalTranscript, pos);
						} else if (interimTranscript) {
							// Insert interim transcript and track its position, to be replaced by final
							tr.insertText(interimTranscript, pos);
							interimTextRef.current = {
								from: pos,
								to: pos + interimTranscript.length,
								text: interimTranscript,
							};
						}

						return tr;
					});
				};

				recognition.onerror = (event: any) => {
					if (event.error === 'network') {
						setErrorMessage('Network error. Please check your connection.');
						// TODO: Maybe retry
					} else if (event.error === 'not-allowed') {
						setErrorMessage('Microphone access denied.');
					} else {
						setErrorMessage(`Recognition error: ${event.error}`);
					}
					setIsListening(false);
				};

				recognition.onend = () => {
					// Only restart if this is still the current recognition instance and we're still listening
					if (recognitionRef.current === recognition && isListening) {
						try {
							recognition.start();
						} catch (error) {
							// If restart fails, stop listening
							setIsListening(false);
						}
					}
				};

				recognition.start();
			} catch (error) {
				setErrorMessage('Failed to start speech recognition');
				setIsListening(false);
			}
		};

		const stopRecognition = () => {
			if (recognitionRef.current) {
				try {
					recognitionRef.current.stop();
				} catch (error) {
					setErrorMessage('Failed to stop speech recognition');
				}
				recognitionRef.current = null;
			}
		};

		if (isListening) {
			startRecognition();
		} else {
			stopRecognition();
		}

		return () => {
			stopRecognition();
		};
	}, [editorApi, isListening]);

	return {
		isListening,
		errorMessage,
	};
};
